import { PolicyDialogMode, WorkflowType } from '@communities/content-management/domain';
import type { Policy } from '@communities/content-management/domain';
import { ErrorBanner } from '@core/components';
import { Dialog, type DialogAction, StepperDots, type StepConfig } from '@soteria/design-system-ve';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { useEffect, useRef } from 'react';

import { dialogHeaderStyles, dialogTitleStyles, dialogContentStyles, dialogStepContentStyles } from './CreatePolicyDialog.css';
import { DialogProvider, useDialogContext, validateStepTransition } from './components/DialogProvider';
import { PolicyInfoStep, TextLabelsStep, ImageLabelsStep, UsernameLabelsStep, CustomLabelsStep, ReviewStep } from './components/Steps';
import { DialogStep } from './types';

/** Switch stepper to compact size when step count exceeds this threshold */
const STEPPER_COMPACT_THRESHOLD = 5;

/**
 * CreatePolicyDialog component props
 */
export interface CreatePolicyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  workflowType: WorkflowType.CLASSIFICATION | WorkflowType.MODERATION;
  policy?: Policy;
  mode?: PolicyDialogMode;
}

/**
 * Dialog step content component
 */
function DialogStepContent({ communityId }: { communityId: string }) {
  const { state, workflowType } = useDialogContext();
  const { labels: standardLabels, loading: labelsLoading } = useStandardLabels();

  switch (state.currentStep) {
    case DialogStep.POLICY_INFO:
      return <PolicyInfoStep />;
    case DialogStep.TEXT_LABELS:
      return <TextLabelsStep labelsLoading={labelsLoading} standardLabels={standardLabels} />;
    case DialogStep.IMAGE_LABELS:
      return <ImageLabelsStep labelsLoading={labelsLoading} standardLabels={standardLabels} />;
    case DialogStep.USERNAME_LABELS:
      return <UsernameLabelsStep labelsLoading={labelsLoading} standardLabels={standardLabels} />;
    case DialogStep.CUSTOM_LABELS:
      // Only rendered for moderation workflow
      if (workflowType === WorkflowType.MODERATION) {
        return <CustomLabelsStep communityId={communityId} />;
      }

      return null;
    case DialogStep.REVIEW:
      return <ReviewStep communityId={communityId} />;
    default:
      return null;
  }
}

/**
 * Build stepper dots steps based on workflow type
 */
function buildStepperSteps(stepOrder: DialogStep[], isEditMode: boolean): StepConfig[] {
  const policyNameRequiredTooltip = 'Please enter a policy name to continue';

  const stepConfigMap: Record<DialogStep, StepConfig> = {
    [DialogStep.POLICY_INFO]: {
      id: 'policy-info',
      label: 'Basic Information',
      description: 'Policy name and settings',
    },
    [DialogStep.TEXT_LABELS]: {
      id: 'text-labels',
      label: 'Text Content',
      description: 'Configure text label rules',
      disabledTooltip: policyNameRequiredTooltip,
    },
    [DialogStep.IMAGE_LABELS]: {
      id: 'image-labels',
      label: 'Image Content',
      description: 'Configure image label rules',
      disabledTooltip: policyNameRequiredTooltip,
    },
    [DialogStep.USERNAME_LABELS]: {
      id: 'username-labels',
      label: 'Username Content',
      description: 'Configure username label rules',
      disabledTooltip: policyNameRequiredTooltip,
    },
    [DialogStep.CUSTOM_LABELS]: {
      id: 'custom-labels',
      label: 'Custom Labels',
      description: 'Select community custom labels',
      disabledTooltip: policyNameRequiredTooltip,
    },
    [DialogStep.REVIEW]: {
      id: 'review',
      label: isEditMode ? 'Save' : 'Review & Create',
      description: isEditMode ? 'Save policy changes' : 'Confirm policy settings',
      disabledTooltip: policyNameRequiredTooltip,
    },
  };

  return stepOrder.map(step => stepConfigMap[step]);
}

/**
 * Custom dialog header component
 */
function DialogHeader({ title }: { title: string }) {
  const { state, actions, isEditMode, stepOrder } = useDialogContext();
  const currentStepIndex = stepOrder.indexOf(state.currentStep);

  const stepperDotsSteps = buildStepperSteps(stepOrder, isEditMode);

  // Auto-size stepper: use compact layout when there are many steps
  const stepperSize = stepOrder.length > STEPPER_COMPACT_THRESHOLD ? 'small' : 'medium';

  const handleStepperClick = (index: number) => {
    const targetStep = stepOrder[index];

    actions.goToStep(targetStep);
  };

  const canNavigateToStepIndex = (fromIndex: number, toIndex: number): boolean => {
    const fromStep = stepOrder[fromIndex];
    const toStep = stepOrder[toIndex];

    return validateStepTransition(fromStep, toStep, { ...state.formData, stepOrder });
  };

  return (
    <div className={dialogHeaderStyles}>
      <h2 className={dialogTitleStyles}>{title}</h2>

      <StepperDots
        interactive
        showDescriptions
        showLabels
        activeStep={currentStepIndex}
        canNavigateToStep={canNavigateToStepIndex}
        size={stepperSize}
        steps={stepperDotsSteps}
        variant="numbers"
        onStepClick={handleStepperClick}
      />
    </div>
  );
}

/**
 * Get step information for navigation
 */
function useStepInfo() {
  const { state, stepOrder } = useDialogContext();
  const currentIndex = stepOrder.indexOf(state.currentStep);

  return {
    isFirst: currentIndex === 0,
    isLast: currentIndex === stepOrder.length - 1,
  };
}

/**
 * Get button labels based on current step, edit mode, and step order
 */
function useButtonLabels(): { next: string; back: string } {
  const { state, isEditMode, isDuplicateMode, stepOrder } = useDialogContext();
  const currentIndex = stepOrder.indexOf(state.currentStep);
  const isLast = currentIndex === stepOrder.length - 1;
  const isFirst = currentIndex === 0;

  // For the last step
  if (isLast) {
    const nextLabel = isEditMode ? 'Save Policy' : isDuplicateMode ? 'Create Copy' : 'Create Policy';
    const prevStep = stepOrder[currentIndex - 1];
    const backLabel = `Back: ${getStepShortLabel(prevStep)}`;

    return { next: nextLabel, back: backLabel };
  }

  // For the first step
  if (isFirst) {
    const nextStep = stepOrder[currentIndex + 1];

    return { next: `Next: ${getStepShortLabel(nextStep)}`, back: 'Back' };
  }

  // For middle steps
  const nextStep = stepOrder[currentIndex + 1];
  const prevStep = stepOrder[currentIndex - 1];

  return {
    next: `Next: ${getStepShortLabel(nextStep)}`,
    back: `Back: ${getStepShortLabel(prevStep)}`,
  };
}

/**
 * Get short label for a step (for button text)
 */
function getStepShortLabel(step: DialogStep): string {
  switch (step) {
    case DialogStep.POLICY_INFO:
      return 'Policy Info';
    case DialogStep.TEXT_LABELS:
      return 'Text Labels';
    case DialogStep.IMAGE_LABELS:
      return 'Image Labels';
    case DialogStep.USERNAME_LABELS:
      return 'Username Labels';
    case DialogStep.CUSTOM_LABELS:
      return 'Custom Labels';
    case DialogStep.REVIEW:
      return 'Review';
    default:
      return 'Next';
  }
}

/**
 * Dialog content wrapper
 */
function DialogContent({ onClose, title, communityId }: { onClose: () => void; title: string; communityId: string }) {
  const { state, actions, isEditMode } = useDialogContext();
  const errorBannerRef = useRef<HTMLDivElement>(null);

  const stepInfo = useStepInfo();
  const buttonLabels = useButtonLabels();

  // Scroll to the error banner when errors appear so it's always visible
  useEffect(() => {
    if (state.errors.length > 0 && errorBannerRef.current) {
      const prefersReducedMotion =
        typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      errorBannerRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
      errorBannerRef.current.focus();
    }
  }, [state.errors.length]);

  const handleNext = () => {
    if (stepInfo.isLast) {
      actions.submitDialog();
    } else {
      actions.goToNextStep();
    }
  };

  const handleBack = () => {
    if (stepInfo.isFirst) {
      onClose();
    } else {
      actions.goToPreviousStep();
    }
  };

  const isNextDisabled = state.isSubmitting || (stepInfo.isLast && (!state.isValid || state.errors.length > 0));

  // Define dialog actions for the fixed footer
  // Note: Dialog renders primaryAction on the left and secondaryAction on the right
  // So we assign Cancel/Back to primaryAction (left) and Next/Create to secondaryAction (right)
  const primaryAction: DialogAction = {
    label: stepInfo.isFirst ? 'Cancel' : buttonLabels.back,
    onClick: handleBack,
    disabled: state.isSubmitting,
    variant: 'secondary',
  };

  const secondaryAction: DialogAction = {
    label: state.isSubmitting && stepInfo.isLast ? (isEditMode ? 'Saving...' : 'Creating...') : buttonLabels.next,
    onClick: handleNext,
    disabled: isNextDisabled,
    variant: 'primary',
  };

  return (
    <Dialog isOpen customHeader={<DialogHeader title={title} />} primaryAction={primaryAction} secondaryAction={secondaryAction} size="xxl" onClose={onClose}>
      <div className={dialogContentStyles}>
        {state.errors.length > 0 && (
          <div ref={errorBannerRef} aria-atomic="true" role="alert" tabIndex={-1}>
            <ErrorBanner errors={state.errors} title="There was an error saving the policy:" />
          </div>
        )}
        <div className={dialogStepContentStyles}>
          <DialogStepContent communityId={communityId} />
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Inner component that only mounts when the dialog is open.
 * This ensures useStandardLabels() only fires a GraphQL query when needed.
 */
function CreatePolicyDialogInner({ onClose, communityId, workflowType, policy, mode = PolicyDialogMode.Create }: Omit<CreatePolicyDialogProps, 'isOpen'>) {
  const { labels: standardLabelsCatalog, loading: labelsLoading } = useStandardLabels();

  if (labelsLoading) {
    return null;
  }

  const isEditMode = mode === PolicyDialogMode.Edit;
  const isDuplicateMode = mode === PolicyDialogMode.Duplicate;
  const dialogTitle = isEditMode ? 'Edit Policy' : isDuplicateMode ? 'Duplicate Policy' : 'Create Policy';

  return (
    <DialogProvider
      communityId={communityId}
      initialPolicy={policy}
      mode={mode}
      standardLabelsCatalog={standardLabelsCatalog}
      workflowType={workflowType}
      onCancel={onClose}
      onComplete={onClose}
    >
      <DialogContent communityId={communityId} title={dialogTitle} onClose={onClose} />
    </DialogProvider>
  );
}

/**
 * CreatePolicyDialog component.
 * Gate on isOpen before mounting the inner component to avoid firing
 * useStandardLabels() GraphQL query when the dialog is closed.
 */
export function CreatePolicyDialog({ isOpen, ...rest }: CreatePolicyDialogProps) {
  if (!isOpen) {
    return null;
  }

  return <CreatePolicyDialogInner {...rest} />;
}
